import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';

import { isSigningIn, useSignIn } from '../../hooks/useSignIn';
import { signInSchema, type SignInValues } from '../../lib/authFormSchema';
import type { LoginCredentials, MfaChallenge } from '../../lib/rudderstack-auth-client';
import { TextLink } from './TextLink';

export function PasswordSignInForm({
  apiBaseUrl,
  returnTo,
  defaultEmail = '',
  onChallenge,
  onSetupRequired,
}: {
  apiBaseUrl: string;
  returnTo: string | null;
  defaultEmail?: string;
  onChallenge: (credentials: LoginCredentials, challenge: MfaChallenge) => void;
  /** Gets the email, so that "Back to sign in" can fill it in again. */
  onSetupRequired: (email: string) => void;
}): React.ReactElement {
  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    mode: 'onTouched',
    defaultValues: { email: defaultEmail, password: '' },
  });
  const signIn = useSignIn(apiBaseUrl, returnTo);
  const busy = isSigningIn(signIn);

  const onValid = (values: SignInValues): void => {
    const credentials = { email: values.email, password: values.password };
    signIn.mutate(credentials, {
      onSuccess: (outcome) => {
        if (outcome.kind === 'mfa_challenge') onChallenge(credentials, outcome.challenge);
        if (outcome.kind === 'mfa_setup') onSetupRequired(values.email);
      },
    });
  };

  return (
    <Form {...form}>
      <form noValidate aria-label="Sign in" className="flex flex-col gap-4" onSubmit={form.handleSubmit(onValid)}>
        {signIn.error && (
          <Callout variant="error" role="alert">
            {signIn.error.message}
          </Callout>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} type="email" size="lg" autoComplete="username" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between gap-2">
                <FormLabel>Password</FormLabel>
                <TextLink to="/forgot-password">Forgot password?</TextLink>
              </div>
              <FormControl>
                <Input {...field} type="password" size="lg" autoComplete="current-password" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </Form>
  );
}
